import {
  normalizeCampaignBrief,
  normalizeCampaignContentSlot,
  type CampaignBriefV1,
  type CampaignContentSlotV1,
  type CampaignSocialCreativeBrief,
} from "@runcheetah/shared";

export type CampaignEditorScheduledPostInput = {
  id: string;
  scheduledFor: string;
  slot?: unknown;
};

export type CampaignEditorInput = {
  name: string;
  startDate: string;
  endDate: string;
  brief?: unknown;
  goals?: string | null;
  audience?: string | null;
  scheduledPosts?: CampaignEditorScheduledPostInput[];
};

export type CampaignEditorAudienceSegment = {
  label: string;
  description: string;
};

export type CampaignEditorPost = {
  id: string;
  scheduledFor: string;
  concept: string;
  objective: CampaignContentSlotV1["objective"];
  objectiveLabel: string;
  audienceSegment: string;
  callToAction: string;
  visualApproach: string;
  platform: CampaignContentSlotV1["platform"];
  postType: CampaignContentSlotV1["postType"];
  productionMode: CampaignContentSlotV1["productionMode"];
  requiredCapabilities: CampaignContentSlotV1["requiredCapabilities"];
  requiresSpecificPersonLikeness: boolean;
  requiresVideo: boolean;
  executionFeasibility: CampaignContentSlotV1["executionFeasibility"];
  socialCreativeBrief?: CampaignSocialCreativeBrief;
};

export type CampaignEditorState = {
  name: string;
  startDate: string;
  endDate: string;
  summary: string;
  strategyRationale: string;
  campaignType: CampaignBriefV1["campaignType"];
  goals: string;
  messagingPillars: string;
  callToActions: string;
  audienceSegments: CampaignEditorAudienceSegment[];
  addressedMarkets: CampaignBriefV1["addressedMarkets"];
  posts: CampaignEditorPost[];
};

export const createCampaignEditorState = (campaign: CampaignEditorInput): CampaignEditorState => {
  const brief = normalizeCampaignBrief({
    briefJson: campaign.brief,
    goals: campaign.goals,
    audience: campaign.audience,
  });

  return {
    name: campaign.name,
    startDate: toDateTimeLocalValue(campaign.startDate),
    endDate: toDateTimeLocalValue(campaign.endDate),
    summary: brief.summary,
    strategyRationale: brief.strategyRationale,
    campaignType: brief.campaignType,
    goals: brief.goals.map((goal) => goal.label).join("\n"),
    messagingPillars: brief.messagingPillars.map((pillar) => pillar.label).join("\n"),
    callToActions: brief.callToActions.join("\n"),
    audienceSegments: brief.audienceSegments.map((segment) => ({
      label: segment.label,
      description: segment.description ?? "",
    })),
    addressedMarkets: brief.addressedMarkets,
    posts: (campaign.scheduledPosts ?? []).map((post) => {
      const slot = normalizeCampaignContentSlot({ slotJson: post.slot });

      return {
        id: post.id,
        scheduledFor: toDateTimeLocalValue(post.scheduledFor),
        concept: slot.concept,
        objective: slot.objective,
        objectiveLabel: slot.objectiveLabel ?? "",
        audienceSegment: slot.audienceSegment ?? "",
        callToAction: slot.callToAction ?? "",
        visualApproach: slot.visualApproach,
        platform: slot.platform,
        postType: slot.postType,
        productionMode: slot.productionMode,
        requiredCapabilities: slot.requiredCapabilities,
        requiresSpecificPersonLikeness: slot.requiresSpecificPersonLikeness,
        requiresVideo: slot.requiresVideo,
        executionFeasibility: slot.executionFeasibility,
        socialCreativeBrief: slot.socialCreativeBrief,
      };
    }),
  };
};

export const buildCampaignUpdateInput = (input: {
  brandId: string;
  campaignId: string;
  state: CampaignEditorState;
}) => ({
  brandId: input.brandId,
  id: input.campaignId,
  name: input.state.name.trim(),
  startDate: fromDateTimeLocalValue(input.state.startDate),
  endDate: fromDateTimeLocalValue(input.state.endDate),
  brief: buildCampaignBrief(input.state),
});

export const buildCampaignScheduleInput = (input: {
  brandId: string;
  campaignId: string;
  state: CampaignEditorState;
}) => ({
  brandId: input.brandId,
  campaignId: input.campaignId,
  scheduledPosts: input.state.posts.map((post) => ({
    id: post.id,
    scheduledFor: fromDateTimeLocalValue(post.scheduledFor),
    slot: buildCampaignSlot(post),
  })),
});

export const getCampaignEditorStateFingerprint = (state: CampaignEditorState): string =>
  JSON.stringify(state);

export const areCampaignEditorStatesEqual = (
  left: CampaignEditorState,
  right: CampaignEditorState,
): boolean => getCampaignEditorStateFingerprint(left) === getCampaignEditorStateFingerprint(right);

const buildCampaignBrief = (state: CampaignEditorState): CampaignBriefV1 => ({
  version: 1,
  campaignType: state.campaignType,
  summary: state.summary.trim(),
  strategyRationale: state.strategyRationale.trim(),
  goals: splitLines(state.goals).map((label) => ({ key: "custom" as const, label })),
  audienceSegments: state.audienceSegments
    .map((segment) => ({
      label: segment.label.trim(),
      description: segment.description.trim() || undefined,
    }))
    .filter((segment) => segment.label.length > 0),
  addressedMarkets: state.addressedMarkets,
  messagingPillars: splitLines(state.messagingPillars).map((label) => ({ label })),
  callToActions: splitLines(state.callToActions),
});

const buildCampaignSlot = (post: CampaignEditorPost): CampaignContentSlotV1 => ({
  version: 1,
  concept: post.concept.trim(),
  objective: post.objective,
  ...(post.objectiveLabel.trim().length > 0 && { objectiveLabel: post.objectiveLabel.trim() }),
  audienceSegment: post.audienceSegment.trim() || null,
  callToAction: post.callToAction.trim() || null,
  visualApproach: post.visualApproach.trim(),
  productionMode: post.productionMode,
  requiredCapabilities: post.requiredCapabilities,
  requiresSpecificPersonLikeness: post.requiresSpecificPersonLikeness,
  requiresVideo: post.requiresVideo,
  executionFeasibility: post.executionFeasibility,
  ...(post.socialCreativeBrief && { socialCreativeBrief: post.socialCreativeBrief }),
  platform: post.platform,
  postType: post.postType,
});

const splitLines = (value: string): string[] =>
  value
    .split(/\r?\n/u)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const padDatePart = (part: number) => String(part).padStart(2, "0");

export const toDateTimeLocalValue = (value: string): string => {
  const date = new Date(value);

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

export const fromDateTimeLocalValue = (value: string): string => new Date(value).toISOString();
